// SaveAsShapeModal — Phase 5 Migration B sub-track (c) UI seed (admin).
//
// 1-field name-input modal triggered from MoneyInvoiceDetailPanel's action
// matrix on an issued invoice. Calls save_invoice_as_shape RPC; toasts the
// outcome via the parent's onResult callback. Phase 6 Wave 6 folds this
// into the cb.RouterSheet grammar.

import { useState } from 'react';
import { Layers } from 'lucide-react';
import { palette, spacing, typography, transition } from '../../styles/tokens';
import { saveInvoiceAsShape } from '../../lib/invoiceShapes';

interface Props {
  isOpen: boolean;
  invoiceId: string;
  onClose: () => void;
  onResult: (ok: boolean, message: string) => void;
}

export function SaveAsShapeModal({ isOpen, invoiceId, onClose, onResult }: Props) {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const canSave = name.trim().length > 0 && !loading;

  const onConfirm = async () => {
    if (!canSave) return;
    setLoading(true);
    const result = await saveInvoiceAsShape({ invoiceId, name: name.trim() });
    setLoading(false);
    if (result.success) {
      onResult(true, 'Saved as a starting point.');
      setName('');
      onClose();
    } else {
      onResult(false, result.error ?? 'Failed to save shape.');
    }
  };

  return (
    <>
      <div
        onClick={loading ? undefined : onClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 70,
          background: 'rgba(0,0,0,0.3)',
          backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
        }}
      />
      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '100%', maxWidth: 420, zIndex: 71,
        background: palette.white, borderRadius: 16,
        boxShadow: '0 16px 64px rgba(0,0,0,0.14), 0 4px 16px rgba(0,0,0,0.08)',
        padding: 28,
      }}>
        <div style={{
          width: 44, height: 44, borderRadius: 14,
          background: palette.blueWash,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: spacing.md,
        }}>
          <Layers size={22} color={palette.blue} strokeWidth={1.8} />
        </div>

        <div style={{ fontSize: 16, fontWeight: 700, color: palette.g800, marginBottom: 6 }}>
          Save as starting point
        </div>
        <div style={{ fontSize: 14, color: palette.g500, lineHeight: 1.5, marginBottom: spacing.md }}>
          Reuse this invoice's line items, terms, and notes on future invoices.
        </div>

        <label style={{ display: 'block', fontSize: typography.size.caption, color: palette.g700, marginBottom: spacing.xs }}>
          Name this shape
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value.slice(0, 80))}
          placeholder="e.g. Standard deep clean"
          autoFocus
          disabled={loading}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canSave) void onConfirm();
          }}
          style={{
            width: '100%', padding: spacing.sm, borderRadius: 8,
            border: `1px solid ${palette.g200}`, fontFamily: 'inherit',
            fontSize: typography.size.caption, color: palette.g800,
            boxSizing: 'border-box',
          }}
        />

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: spacing.md }}>
          <button onClick={onClose} disabled={loading} style={{
            padding: '9px 22px', borderRadius: 20,
            border: `1px solid ${palette.g200}`, background: palette.white,
            fontSize: 13.5, fontWeight: 500, color: palette.g600,
            cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
            transition: `background ${transition.fast}`,
          }}>Cancel</button>
          <button onClick={() => void onConfirm()} disabled={!canSave} style={{
            padding: '9px 22px', borderRadius: 20, border: 'none',
            background: canSave ? palette.blue : palette.g300,
            color: palette.white, fontSize: 13.5, fontWeight: 600,
            cursor: canSave ? 'pointer' : 'not-allowed', fontFamily: 'inherit',
          }}>{loading ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </>
  );
}
